import { css } from '@emotion/react'
import { Alert, Cell, Grid, Icon, Text, Tooltip } from 'bold-ui'
import {
  CboSelectField,
  CboSelectModel,
  CheckboxField,
  createDecorator,
  EquipeSelectField,
  EquipeSelectModel,
  Form,
  FormPrompt,
  FormProps,
  FormRenderProps,
  PerfilSelectField,
  PerfilSelectModel,
  UnidadeSaudeSelectField,
  UnidadeSaudeSelectModel,
} from 'components/form'
import { DefaultFormFooter } from 'components/form/FormFooter'
import { TipoAcesso } from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'
import { createValidator, required } from 'util/validation'

const validator = createValidator<LotacaoFormModel>({
  unidadeSaude: [required],
  cbo: [required],
  perfis: [required],
})

export interface LotacaoFormProps extends FormProps<LotacaoFormModel> {
  disableFields?: boolean
  importada?: boolean
  cadastro?: boolean
}

export interface LotacaoFormModel {
  ativo: boolean
  cbo: CboSelectModel
  equipe: EquipeSelectModel
  unidadeSaude: UnidadeSaudeSelectModel
  perfis: ReadonlyArray<PerfilSelectModel>
  profissional: ID
  atualizaPerfil: boolean
}

const path = metaPath<LotacaoFormModel>()

const tooltipText =
  'Habilitar a sincronização ao importar CNES atualizará a lotação conforme o arquivo do CNES e adicionará os perfis padrão do sistema para esta CBO.'

const calculator = createDecorator({
  field: path.unidadeSaude.absolutePath(),
  updates: {
    [path.equipe.absolutePath()]: (unidadeSaude, allValues, prevValues) => {
      if (Object.entries(prevValues).length === 0) {
        return allValues[path.equipe.absolutePath()]
      }
      return undefined
    },
  },
})

export function LotacaoForm(props: LotacaoFormProps) {
  const { onSubmit, onSubmitSucceeded, initialValues, disableFields, importada, cadastro } = props

  const renderForm = (formProps: FormRenderProps) => {
    const value = formProps.values as LotacaoFormModel
    return (
      <form onSubmit={formProps.handleSubmit} noValidate>
        <FormPrompt />
        <Grid wrap>
          <Cell xs={12} md={6}>
            <UnidadeSaudeSelectField
              disabled={disableFields}
              label='Unidade de Saúde'
              name={path.unidadeSaude.absolutePath()}
              required
              somenteUnidadesPermitidas={true}
            />
          </Cell>
          <Cell xs={12} md={6}>
            <CboSelectField
              label='CBO'
              name={path.cbo.absolutePath()}
              mostrarIndisponivelLotacao={false}
              disabled={disableFields}
              required
            />
          </Cell>
          <Cell xs={12} md={6}>
            <EquipeSelectField
              label='Equipe'
              name={path.equipe.absolutePath()}
              disabled={disableFields || !value.unidadeSaude}
              unidadeSaudeId={value.unidadeSaude && value.unidadeSaude.id}
            />
          </Cell>
          <Cell xs={12} md={6}>
            <PerfilSelectField tipoAcesso={TipoAcesso.LOTACAO} label='Perfis' name={path.perfis} multiple required />
          </Cell>
          <Cell style={styles.inlineItensCell}>
            <CheckboxField
              label='Sincronizar lotação ao importar CNES'
              name={path.atualizaPerfil}
              style={styles.marginCheckbox}
            />
            <Tooltip text={tooltipText}>
              <Icon icon='infoCircleFilled' size={1} />
            </Tooltip>
          </Cell>
          {(!importada || cadastro) && (
            <Cell>
              <Alert
                type='info'
                inline
                styles={{
                  wrapper: styles.alertWrapper,
                  container: styles.alertContainer,
                }}
              >
                {cadastro ? (
                  <>
                    <Text fontWeight='bold' style={styles.colorTextBold}>
                      Lotações criadas por esta funcionalidade não estão vinculadas ao CNES.
                    </Text>{' '}
                    Elas só serão sincronizadas na importação se estiverem informadas no arquivo do CNES e se o checkbox
                    “Sincronizar lotação ao importar CNES” estiver selecionado.
                  </>
                ) : (
                  <>
                    <Text fontWeight='bold' style={styles.colorTextBold}>
                      Essa lotação não está vinculada ao CNES.
                    </Text>{' '}
                    Ela só será sincronizada na importação se estiver informada no arquivo do CNES e se o checkbox
                    “Sincronizar lotação ao importar CNES” estiver selecionado.
                  </>
                )}
              </Alert>
            </Cell>
          )}
        </Grid>

        <DefaultFormFooter handleSubmit={formProps.handleSubmit} />
      </form>
    )
  }

  return (
    <Form
      decorators={[calculator]}
      render={renderForm}
      validate={validator}
      onSubmit={onSubmit}
      onSubmitSucceeded={onSubmitSucceeded}
      initialValues={initialValues}
    />
  )
}

const styles = {
  inlineItensCell: css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `,
  marginCheckbox: css`
    & input[type='checkbox'] {
      margin-left: 0;
    }
  `,
  alertWrapper: css`
    padding: 0.375rem;
  `,
  alertContainer: css`
    align-items: center;
  `,
  colorTextBold: css`
    color: inherit;
  `,
}
